import LZString from 'lz-string'
import type { JSONObject, JSONValue } from 'src/graphql.generated'

/**
 * Marker key used to identify values that have been encoded for WAF bypass.
 * The server uses this to know which values need to be decoded.
 */
export const WAF_ENCODED_MARKER = '__wafEncoded'

/**
 * Encodes a string value using LZ-String compression to bypass Cloud Armor WAF rules.
 * This prevents false positives from patterns like "<script>", "eval()", etc.
 * that may appear in clinical notes.
 */
export const encodeForWaf = (value: string): string => LZString.compressToEncodedURIComponent(value)

/**
 * Decodes a WAF-encoded string back to its original value.
 */
export const decodeFromWaf = (encoded: string): string => {
  const decoded = LZString.decompressFromEncodedURIComponent(encoded)
  if (decoded === null) {
    throw new Error('Failed to decode WAF-encoded string')
  }
  return decoded
}

/**
 * Recursively encodes all string values in an object for WAF bypass.
 * Adds a marker to indicate the object has been encoded.
 *
 * @param value - The object to encode (typically patient history value)
 * @returns A new object with all string values encoded, compatible with JSONObject
 */
export const encodeObjectForWaf = (value: Record<string, unknown>): JSONObject => {
  const encoded = encodeObjectStrings(value) as JSONObject
  return {
    ...encoded,
    [WAF_ENCODED_MARKER]: true,
  }
}

/**
 * Helper function to recursively encode all string values in an object.
 * Follows JSON.stringify semantics: undefined values in objects are omitted.
 */
const encodeObjectStrings = (input: unknown): JSONValue | undefined => {
  if (input === null) {
    return null
  }

  // Return undefined as-is - it will be omitted from object properties
  // This matches JSON.stringify behavior where undefined properties are skipped
  if (input === undefined) {
    return undefined
  }

  if (typeof input === 'string') {
    // Only encode non-empty strings
    return input.length > 0 ? encodeForWaf(input) : input
  }

  if (typeof input === 'number' || typeof input === 'boolean') {
    return input
  }

  if (Array.isArray(input)) {
    // For arrays, undefined elements become null (JSON.stringify behavior)
    return input.map((item) => {
      const encoded = encodeObjectStrings(item)
      return encoded === undefined ? null : encoded
    })
  }

  if (typeof input === 'object') {
    // Skip undefined values in objects (JSON.stringify behavior)
    return Object.entries(input).reduce<JSONObject>((acc, [key, val]) => {
      const encodedVal = encodeObjectStrings(val)
      // Omit undefined values, matching JSON.stringify behavior
      if (encodedVal === undefined) {
        return acc
      }
      return {
        ...acc,
        [key]: encodedVal,
      }
    }, {})
  }

  // For any other types, throw - this indicates invalid input
  // that shouldn't exist in JSON-compatible data
  throw new Error(`encodeObjectStrings received unsupported type: ${typeof input}`)
}
